import {
  DatePicker,
  Drawer,
  Form,
  Input,
  Upload,
  Button,
  message,
  UploadProps,
} from "antd";
import { useEffect, useState } from "react";
import { AfBlobPath } from "../../../../graphql/operations/@types";
import { renderAntDMessageConfig } from "../../../../utils/renderAntDMessageConfig";
import { UploadOutlined } from "@ant-design/icons";
import { useUploadFileMutation } from "../../../../graphql/operations/upload-file";
import { useAddTev2MonthlySongMutation } from "../../../../graphql/operations/clubApp/TEV2/addTev2MonthlySong";
import { useUpdateTev2MonthlySongMutation } from "../../../../graphql/operations/clubApp/TEV2/updateTev2MonthlySong";
import requiredRule from "../../../../utils/formRules/requiredRule";
import dayjs from "dayjs";
import "./TEV2Drawer.scss";

interface TEV2DataType {
  seq?: number | undefined | null;
  year: number;
  month: number;
  tev2Banner: string | undefined | null;
  introductionBanner: string | undefined | null;
  vimeoId: string | undefined | null;
  tev2SongId: string | undefined | null;
}

interface Props {
  tEV2DrawerOpen: boolean;
  isCreate: boolean;
  setTEV2DrawerOpen: any;
  TEV2Refetch: any;
  selectedTEV2Video: TEV2DataType | undefined;
  setSelectedTEV2Video: any;
}

const TEV2Drawer = ({
  tEV2DrawerOpen,
  isCreate,
  setTEV2DrawerOpen,
  TEV2Refetch,
  selectedTEV2Video,
  setSelectedTEV2Video,
}: Props) => {
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tev2ImageFileObject, setTev2ImageFileObject] = useState<any>(null);
  const [noTev2ImageWhenEditing, setNoTev2ImageWhenEditing] = useState(false);
  const [introImageFileObject, setIntroImageFileObject] = useState<any>(null);
  const [noIntroImageWhenEditing, setNoIntroImageWhenEditing] = useState(false);

  const [uploadFile] = useUploadFileMutation();
  const [addTev2MonthlySong] = useAddTev2MonthlySongMutation();
  const [updateTev2MonthlySong] = useUpdateTev2MonthlySongMutation();
  const [form] = Form.useForm();

  const renameFile = (file: any, yearMonth: string, isIntro: boolean) => {
    if (!file) {
      return;
    }
    const fileBlob = file.originFileObj || file;

    // 創建新的 File 物件
    return new File(
      [fileBlob],
      `${isIntro ? "Introduction_Banner_" : "TEV2_Banner_"}${yearMonth}.png`,
      {
        type: fileBlob.type,
      }
    );
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    const { yearMonth, vimeoId, songId } = form.getFieldsValue();
    const yearMonthValue = dayjs(yearMonth).format("YYYYMM");
    const year = dayjs(yearMonth).year();
    const month = dayjs(yearMonth).month() + 1;

    const promises = [];

    if (tev2ImageFileObject) {
      const uploadTev2ImagePromise = uploadFile({
        variables: {
          blobs: [
            {
              file: renameFile(tev2ImageFileObject, yearMonthValue, false),
              path: AfBlobPath.Tev2TeBanners,
            },
          ],
        },
      });
      promises.push(uploadTev2ImagePromise);
    }

    // if (introImageFileObject) {
    //   const uploadIntroImagePromise = uploadFile({
    //     variables: {
    //       blobs: [
    //         {
    //           file: renameFile(introImageFileObject, yearMonthValue, true),
    //           path: AfBlobPath.Tev2TeBanners,
    //         },
    //       ],
    //     },
    //   });
    //   promises.push(uploadIntroImagePromise);
    // }

    Promise.all(promises)
      .then(() => {
        if (isCreate) {
          addTev2MonthlySong({
            variables: {
              year: year,
              month: month,
              tev2Banner: tev2ImageFileObject
                ? `TEV2_Banner_${yearMonthValue}.png`
                : "",
              introductionBanner: "",
              vimeoId: vimeoId,
              tev2SongId: songId,
            },
          }).then(() => {
            setIsSubmitting(false);
            closeDrawer();
            TEV2Refetch();
            message.success("Successfully added");
          });
        } else {
          updateTev2MonthlySong({
            variables: {
              seq: selectedTEV2Video?.seq || 0,
              year: year,
              month: month,
              tev2Banner:
                !tev2ImageFileObject &&
                (noTev2ImageWhenEditing || !selectedTEV2Video?.tev2Banner)
                  ? ""
                  : `TEV2_Banner_${yearMonthValue}.png`,
              introductionBanner: "",
              vimeoId: vimeoId,
              tev2SongId: songId,
            },
          }).then(() => {
            setIsSubmitting(false);
            closeDrawer();
            TEV2Refetch();
          });
        }
      })
      .catch((e) => console.log(e));
  };

  const closeDrawer = () => {
    setTEV2DrawerOpen(false);
    form.resetFields();
    setIntroImageFileObject(null);
    setTev2ImageFileObject(null);
    setNoIntroImageWhenEditing(false);
    setNoTev2ImageWhenEditing(false);
    setSelectedTEV2Video(null);
  };

  const imageUploadProps = (name: string): UploadProps => ({
    name: "file",
    action: "https://httpbin.org/post",
    headers: {
      authorization: "authorization-text",
    },
    beforeUpload: (file) => {
      const isPNG = file.type === "image/png";
      if (!isPNG) {
        message.error(renderAntDMessageConfig("Only PNG files are allowed!"));
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    onChange(info) {
      if (info.file.status !== "uploading") {
        if (!isImageUploading) {
          setIsImageUploading(true);
        }
      }
      if (info.file.status === "done") {
        message.success(
          renderAntDMessageConfig(
            `${info.file.name} file uploaded successfully`
          )
        );
        setIsImageUploading(false);
        if (name === "tev2") {
          setTev2ImageFileObject(info.file.originFileObj);
        } else {
          setIntroImageFileObject(info.file.originFileObj);
        }
      } else if (info.file.status === "error") {
        message.error(
          renderAntDMessageConfig(`${info.file.name} file upload failed.`)
        );
      }
    },
    onRemove: () => {
      if (name === "tev2") {
        setTev2ImageFileObject(null);
        setNoTev2ImageWhenEditing(true);
      } else {
        setIntroImageFileObject(null);
        setNoIntroImageWhenEditing(true);
      }
    },
    maxCount: 1,
  });

  useEffect(() => {
    if (selectedTEV2Video) {
      form.setFieldsValue({
        yearMonth: dayjs(
          `${selectedTEV2Video.year}-${selectedTEV2Video.month}`
        ),
        vimeoId: selectedTEV2Video.vimeoId,
        songId: selectedTEV2Video.tev2SongId,
      });

      if (selectedTEV2Video.tev2Banner) {
        form.setFieldsValue({
          tev2Banner: [
            {
              uid: "-1",
              name: `TEV2_Banner_${selectedTEV2Video.year}${
                selectedTEV2Video.month > 10
                  ? selectedTEV2Video.month + ""
                  : "0" + selectedTEV2Video.month
              }.png`,
              status: "done",
              url: selectedTEV2Video.tev2Banner,
            },
          ],
        });
      }

      if (selectedTEV2Video.introductionBanner) {
        form.setFieldsValue({
          introductionBanner: [
            {
              uid: "-2",
              name: `Introduction_Banner_${selectedTEV2Video.year}${
                selectedTEV2Video.month > 10
                  ? selectedTEV2Video.month + ""
                  : "0" + selectedTEV2Video.month
              }.png`,
              status: "done",
              url: selectedTEV2Video.introductionBanner,
            },
          ],
        });
      }
    }
  }, [selectedTEV2Video, form]);

  return (
    <Drawer
      open={tEV2DrawerOpen}
      onClose={closeDrawer}
      title={`${isCreate ? "Create new" : "Edit"} Song of the Month content`}
      width={500}
      id="tev2DrawerWrapper"
      footer={
        <div className="flex justify-end">
          <Button onClick={closeDrawer}>Cancel</Button>
          <Button
            className="ml-4"
            type="primary"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isImageUploading}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Year month" rules={[requiredRule]} name="yearMonth">
          <DatePicker className="w-full" picker="month" format="YYYY-MMM" />
        </Form.Item>
        <Form.Item label="Vimeo ID" rules={[requiredRule]} name="vimeoId">
          <Input placeholder="Vimeo ID" />
        </Form.Item>
        <Form.Item label="Song ID" rules={[requiredRule]} name="songId">
          <Input placeholder="Song ID" />
        </Form.Item>
        <Form.Item
          label="TEV2 banner"
          name="tev2Banner"
          valuePropName="fileList"
          getValueFromEvent={(e) => e.fileList}
        >
          <Upload {...imageUploadProps("tev2")}>
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>
        <p className="warning-text first-line">Formats: PNG only.</p>
        <p className="warning-text bottom-line">Recommended size: 800*300px</p>
        {/* <Form.Item
          label="Introduction banner"
          name="introductionBanner"
          valuePropName="fileList"
          getValueFromEvent={(e) => e.fileList}
        >
          <Upload {...imageUploadProps("introduction")}>
            <Button icon={<UploadOutlined />}>Click to Upload</Button>
          </Upload>
        </Form.Item>
        <p className="warning-text first-line">Formats: PNG only.</p>
        <p className="warning-text bottom-line">Recommended size: 720*400px</p> */}
      </Form>
    </Drawer>
  );
};

export default TEV2Drawer;
